import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../../components/Config/api.service";
import { MdOutlineUploadFile } from "react-icons/md";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import EmployeeSelect from "../../../components/Formulaire/EmployeeSelect";
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import Modal from '../../../components/Utils/ModalShow';
import AmountInput from "../../../components/Utils/AmountInput";
import AccountSelectField from '../../Settings/Tabs/Account/AccountSelectField';

const NoteFrais = ({ id, setErrorMessage, setSuccessMessage, closeEdit }) => {
    const { t } = useTranslation();
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        amount: "",
        date: "",
        description: "",
        account_debit: '',
        account_credit: '',
        not_computed_in_net: false,
    });
    const [existingDocument, setExistingDocument] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const noteResponse = id ? await apiService.getElement(`/note/${id}`) : null;

                if (noteResponse) {
                    const note = noteResponse.data.note;
                    setFormData({
                        name: note.name,
                        amount: note.amount,
                        date: note.date,
                        description: note.description || "",
                    });
                    setSelectedEmployee({
                        value: note.employee_id,
                        label: note.employee_name,
                    });
                    setUploadedFile(null);
                    setExistingDocument(note.has_document);
                }
            } catch (error) {
                setErrorMessage(t('noteFrais.fetchError'));
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, setErrorMessage]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleMontantChange = (newValue) => {
        setFormData((prev) => ({ ...prev, amount: newValue }));
    };

    // Handle file upload
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.size > 25 * 1024 * 1024) {
            setErrorMessage(t('noteFrais.fileTooLarge'));
            return;
        }
        setUploadedFile(file);
    };

    // Validate form data
    const validateForm = () => {
        if (!selectedEmployee) return t('noteFrais.selectEmployeeRequired');
        if (!formData.name) return t('noteFrais.labelRequired');
        if (!formData.amount || parseFloat(formData.amount) <= 0) return t('noteFrais.validAmountRequired');
        if (!formData.date) return t('noteFrais.dateRequired');
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const error = validateForm();
        if (error) {
            setErrorMessage(error);
            return;
        }

        setIsSubmitting(true);

        try {
            let payload;
            let sendFile = false;

            if (uploadedFile) {
                // Utilisation de FormData pour multipart
                payload = new FormData();
                payload.append("name", formData.name);
                payload.append("amount", formData.amount);
                payload.append("date", formData.date);
                payload.append("description", formData.description);
                payload.append("employee_id", selectedEmployee.value);
                payload.append("supporting_document", uploadedFile);
                sendFile = true;
            } else {
                // Envoi classique JSON
                payload = {
                    ...formData,
                    employee_id: selectedEmployee.value,
                };
            }

            if (id) {
                await apiService.updateElementById("/note", id, payload, sendFile);
            } else {
                await apiService.saveElement("/note", payload, sendFile);
                setFormData({ name: "", amount: "", date: "", description: "" });
                setSelectedEmployee(null);
                setUploadedFile(null);
            }
            closeEdit();
        } catch (error) {
            setErrorMessage(t('noteFrais.saveError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadExistingDocument = async () => {
        try {
            const response = await apiService.getBlobFile(`/note/${id}/download`);
            const blob = new Blob([response.data], { type: response.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "document"; // ou le nom du fichier que tu veux
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage(t('noteFrais.downloadError'));
        }
    };

    const handleArchive = async () => {
        setIsArchiveModalOpen(false);
        setIsSubmitting(true);
        try {
            await apiService.updateElementById(`/note`, id, { active: false });
            setSuccessMessage && setSuccessMessage(t('basePrimeForm.archiveSuccess'));
            closeEdit();
        } catch (error) {
            setErrorMessage(t('noteFrais.archiveError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const dropdownActions = [
        {
            label: t('basePrimeForm.archive'),
            onClick: () => setIsArchiveModalOpen(true),
            disabled: isSubmitting || !id,
            color: 'text-red-500',
            icon: <FiTrash2 size={16} />
        }
    ];

    const loadAccountOptions = async (inputValue) => {
        if (inputValue.length < 2) return [];
        try {
            const response = await apiService.getElement(`/accounting/accounts/search?q=${inputValue}`);
            return response.data.map((account) => ({
                value: account.code,
                label: `${account.code} - ${account.name}`,
                account: account,
            }));
        } catch (error) {
            console.error("Erreur lors de la recherche des comptes:", error);
            return [];
        }
    };

    return (
        <>
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className='flex justify-between items-center mb-6'>
                <h1 className="text-2xl font-bold flex items-center">
                    <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                    {t('noteFrais.title')}
                </h1>
                <div>
                    <button
                        type="button"
                        className="bg-orange-200 text-black rounded-full btn mx-2"
                        onClick={closeEdit}
                    >
                        {t('basePrimeForm.cancel')}
                    </button>
                    <button
                        type="submit"
                        className="bg-[--color-primary] text-white hover:bg-purple-500 rounded-full btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <ClipLoader color='white' size={20} /> : t('basePrimeForm.save')}
                    </button>
                    <ActionDropdown actions={dropdownActions} />
                </div>
            </div>

            {/* Section Informations générales */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.generalInfo')}</h3>
                    <p className="text-sm text-gray-600 mt-1">{t('noteFrais.basicDetails')}</p>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="employee" className="block text-sm font-medium text-gray-700">
                                {t('noteFrais.employee')}
                            </label>
                            <EmployeeSelect
                                value={selectedEmployee}
                                onChange={setSelectedEmployee}
                                isDisabled={isSubmitting}
                                placeholder={t('noteFrais.chooseEmployee')}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                                {t('noteFrais.date')}
                            </label>
                            <input
                                type="date"
                                id="date"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                value={formData.date}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                {t('noteFrais.reason')}
                            </label>
                            <input
                                type="text"
                                id="name"
                                placeholder={t('noteFrais.descriptionPlaceholder')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                                {t('noteFrais.amount')}
                            </label>
                            <AmountInput
                                value={formData.amount}
                                onChange={handleMontantChange}
                                placeholder={t('noteFrais.amountPlaceholder')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            {t('noteFrais.optionalDescription')}
                        </label>
                        <textarea
                            id="description"
                            placeholder={t('noteFrais.detailedDescription')}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Section Document justificatif */}
                    <div className="space-y-2">
                        <label htmlFor="justificatif" className="block text-sm font-medium text-gray-700">
                            {t('noteFrais.supportingDocument')}
                        </label>
                        <div className="flex gap-4 items-center justify-center border-2 border-gray-300 border-dashed rounded-lg p-6 hover:border-indigo-300 transition-colors duration-200">
                            <div className="bg-purple-100 rounded-full p-3">
                                <MdOutlineUploadFile className="text-3xl text-[--color-primary]" />
                            </div>
                            <div className="text-center">
                                <label
                                    htmlFor="file-upload"
                                    className="cursor-pointer font-semibold text-[--color-primary] hover:text-purple-700 transition-colors duration-200"
                                >
                                    {uploadedFile ? t('noteFrais.replaceDocument') : t('noteFrais.clickToUpload')}
                                </label>
                                <input
                                    id="file-upload"
                                    type="file"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    disabled={isSubmitting}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                <p className="text-sm text-gray-500 mt-1">{t('noteFrais.orDragAndDrop')}</p>
                                <p className="text-xs text-gray-400 mt-1">{t('noteFrais.maxFileSize')}</p>
                            </div>
                        </div>
                        {uploadedFile && (
                            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex-1">
                                    <p className="text-sm text-green-800 font-medium">{t('noteFrais.selectedFile')}</p>
                                    <p className="text-sm text-green-600">{uploadedFile.name}</p>
                                </div>
                            </div>
                        )}
                        {existingDocument && !uploadedFile && (
                            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex-1">
                                    <p className="text-sm text-blue-800 font-medium">{t('noteFrais.existingDocument')}</p>
                                    <button
                                        type="button"
                                        onClick={downloadExistingDocument}
                                        className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                                    >
                                        {t('noteFrais.downloadExisting')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Configuration comptable */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.accountingConfig')}</h3>
                            <p className="text-sm text-gray-600 mt-1">{t('basePrimeForm.accountingConfigSubtitle')}</p>
                        </div>
                        <div className="text-gray-400">
                            {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isAccountSectionExpanded && (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AccountSelectField
                                label={t('basePrimeForm.debitAccount')}
                                value={formData.account_debit}
                                onChange={(option) => setFormData(prev => ({ ...prev, account_debit: option ? option.value : '' }))}
                                placeholder={t('basePrimeForm.search')}
                                loadAccountOptions={loadAccountOptions}
                            />
                            <AccountSelectField
                                label={t('basePrimeForm.creditAccount')}
                                value={formData.account_credit}
                                onChange={(option) => setFormData(prev => ({ ...prev, account_credit: option ? option.value : '' }))}
                                placeholder={t('basePrimeForm.search')}
                                loadAccountOptions={loadAccountOptions}
                            />
                        </div>

                        {/* Séparateur visuel */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-md font-medium text-gray-800 mb-4">{t('basePrimeForm.calculationOptions')}</h4>
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <h5 className="font-medium text-gray-800">{t('basePrimeForm.notComputedInNet')}</h5>
                                    <p className="text-sm text-gray-600 mt-1">{t('noteFrais.notComputedInNetHelp')}</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="not_computed_in_net"
                                        checked={!!formData.not_computed_in_net}
                                        onChange={e => setFormData(prev => ({ ...prev, not_computed_in_net: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>

        <Modal
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            title={t('basePrimeForm.archiveConfirmTitle')}
            footer={
                <>
                    <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>{t('basePrimeForm.cancel')}</button>
                    <button className="btn btn-error" onClick={handleArchive}>{t('basePrimeForm.confirm')}</button>
                </>
            }
        >
            <p>{t('noteFrais.archiveConfirmQuestion')}</p>
        </Modal>
        </>
    );
};

export default NoteFrais;